import type { ReactNode } from 'react'
import { Icon } from './Icon'
import type { IconName } from './icons/iconData'
import type { ColorToken } from '@/styles/tokens'

type AlertVariant = 'danger' | 'info' | 'warn'

/**
 * `div.alert` → `alert` (process/08-COMPONENT-CATALOGUE.md), `info`/`warn` variants no-suffix
 * default is `danger`. gap 10px → --sp-5 (0 delta); border-radius 4px → --r-2 (0 delta); padding
 * 10px 12px → --sp-5 / --sp-6 (0 delta both); font-size 12px → --fs-body (0 delta); line-height
 * 1.5 → --lh-normal (0 delta). Border widths (1px, the 3px left accent) are not a token family.
 *
 * **`icon` had an `undefined` default and no caller ever passed one (W156) — `data-cmp="alert"`
 * satisfied its own catalogue entry, but the "icon in alert" entry every one of this component's
 * callers' own specs also list never did.** Now defaults per variant (`i-alert`/`i-info`) rather
 * than requiring 30-odd call sites to each choose one; the prop still lets a caller override it.
 */
export function Alert({ variant = 'danger', icon, children }: { variant?: AlertVariant; icon?: ReactNode; children: ReactNode }) {
  const byVariant: Record<AlertVariant, { border: string; bg: string; accent: string; iconName: IconName; color: ColorToken }> = {
    danger: { border: 'border-danger-bd', bg: 'bg-danger-bg', accent: 'var(--accent-danger)', iconName: 'i-alert', color: 'danger-bd' },
    info: { border: 'border-info-bd', bg: 'bg-info-bg', accent: 'var(--blue)', iconName: 'i-info', color: 'blue' },
    warn: { border: 'border-warn-bd', bg: 'bg-warn-bg', accent: 'var(--orange)', iconName: 'i-alert', color: 'orange' },
  }
  const v = byVariant[variant]
  return (
    <div
      data-cmp="alert"
      role={variant === 'danger' ? 'alert' : undefined}
      className={`flex gap-5 items-start border ${v.border} ${v.bg} rounded-2 py-5 px-6 text-body text-tx2 leading-normal`}
      style={{ borderLeft: `3px solid ${v.accent}` }}
    >
      {icon ?? <Icon name={v.iconName} color={v.color} />}
      <div>{children}</div>
    </div>
  )
}
